import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { STRINGS } from "@/i18n";
import { MistContext } from "@/mist";
import Starfield from "@/components/Starfield";
import OracleHero from "@/components/OracleHero";
import Welcome from "@/components/Welcome";
import Footer from "@/components/Footer";
import SoundToggle from "@/components/SoundToggle";
import VoiceToggle from "@/components/VoiceToggle";
import MistTransition from "@/components/MistTransition";
import { unlockAudio, setAmbience } from "@/audio";

const Shell = () => {
  const [lang, setLang] = useState("fr");
  const [voiceOn, setVoiceOn] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [mist, setMist] = useState(false);
  const t = STRINGS[lang];
  const lenisRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const toggleSound = () => {
    const next = !soundOn;
    setAmbience(next);
    setSoundOn(next);
    setVoiceOn(next);
  };

  const enterWithMist = useCallback(
    (path) => {
      setMist(true);
      setTimeout(() => navigate(path), 1000);
      setTimeout(() => setMist(false), 1700);
    },
    [navigate]
  );

  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <MistContext.Provider value={enterWithMist}>
      <div className="App bg-[#0D0726]">
        <Starfield />
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-[1]"
          style={{
            background:
              "radial-gradient(ellipse 90% 60% at 50% 38%, rgba(139,92,246,0.16) 0%, rgba(245,158,11,0.05) 45%, transparent 75%)",
          }}
        />
        <div className="grain-overlay" aria-hidden="true" />
        <MistTransition active={mist} />
        <header className="fixed right-5 top-5 z-50 flex items-center gap-2">
          <VoiceToggle t={t} voiceOn={voiceOn} onToggle={() => setVoiceOn((v) => !v)} />
          <SoundToggle t={t} on={soundOn} onToggle={toggleSound} />
          <div
            data-testid="lang-toggle"
            className="flex rounded-full border border-white/10 bg-black/40 p-1 backdrop-blur-xl"
          >
            {["fr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.2em] transition-[background-color,color] duration-300 ${
                  lang === l ? "bg-amber-500 text-[#030014]" : "text-white/60 hover:text-white/90"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </header>
        <main className="relative">
          <Routes>
            <Route path="/" element={<Welcome t={t} />} />
            <Route
              path="/oracle"
              element={
                <>
                  <OracleHero t={t} voiceOn={voiceOn} />
                  <Footer t={t} />
                </>
              }
            />
            <Route path="*" element={<Welcome t={t} />} />
          </Routes>
        </main>
      </div>
    </MistContext.Provider>
  );
};

const App = () => (
  <BrowserRouter>
    <Shell />
  </BrowserRouter>
);

export default App;
